import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { ACTIONS, ACTIVITY_STATUS, FILTERS, STATUS_MAPPING } from "../../../common/Constants";
import { useGetAllActivities } from "../../../../backend/query";
import { ACTIVITY_TYPE, ACTIVITY_TYPE_ICONS, API_DELIMITER } from "../../../../utils/constants";
import Table, { CellTmpl, DEFAULT_PAYLOAD, TitleTmpl, reactFormatter } from "../../../common/Table";
import AdvanceSearch from "../../../common/AdvanceSearch";
import { preventDefault } from "../../../../utils/common";
import AlertModal from "../../../common/AlertModal";
import Icon from "../../../common/Icon";
import AdminLocations from "./AdminLocations";
import UnBlockModal from "./UnBlockModal";
import BulkUnBlockModal from "./BulkUnBlockModal";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { GetAuditScheduleBreadcrumb } from "./Companies.constants";
import MastersLayout from "../MastersLayout";

const SortFields: any = {
    'act.name': 'act_name',
    'rule.name': 'rule)nname',
    'activity.name': 'activity_name',
    'associate_company.name': 'associate_company_name',
    'location.name': 'location_name',
    'activity.activity_type': 'activity_activity_type'
};

const INCLUDE_ASSOCIATION_COLUMNS = {
    act: { only: ['name'] },
    rule: { only: ['name'] },
    activity: { only: ['name', 'activity_type'] },
    associate_company: { only: ['name'] },
    location: { only: ['name'] }
};

const INCLUDES_ASSOCIATIONS = ['act', 'rule', 'activity', 'associate_company', 'location'];

function getDefaultPayload() {
    const fromDate = new Date();
    fromDate.setFullYear(2000);
    const toDate = new Date();
    toDate.setHours(23);
    toDate.setMinutes(59);
    toDate.setSeconds(59);
    toDate.setMilliseconds(999);
    toDate.setDate(toDate.getDate() - 1);
    return [
        { columnName: 'published', value: 'false' },
        { columnName: 'fromDate', value: dayjs(new Date(fromDate)).local().format() },
        { columnName: 'toDate', value: dayjs(new Date(toDate)).local().format() },
        { columnName: 'status', value: [ACTIVITY_STATUS.OVERDUE, ACTIVITY_STATUS.REJECTED].join(API_DELIMITER) }
    ]
}

function LockUnLock() {
    const [breadcrumb] = useState(GetAuditScheduleBreadcrumb('Location Audit Un-Block Activities'));
    const [activity, setActivity] = useState<any>();
    const [action, setAction] = useState(ACTIONS.NONE);
    const [data, setData] = useState<any>();
    const [params, setParams] = useState<any>();
    const [locationFilters, setLocationFilter] = useState<any>();
    const lfRef: any = useRef();
    lfRef.current = locationFilters;
    const [advaceSearchFilters, setAdvanceSearchFilters] = useState<any>();
    const afRef: any = useRef();
    afRef.current = advaceSearchFilters;
    const [statusFilters] = useState({ status_in: [ACTIVITY_STATUS.OVERDUE, ACTIVITY_STATUS.REJECTED]});
    const sfRef: any = useRef();
    sfRef.current = statusFilters;
    const [payload, setPayload] = useState<any>();
    const payloadRef: any = useRef();
    payloadRef.current = payload;
    const { activities, total, isFetching, refetch } = useGetAllActivities(payload, Boolean(hasFilters(null, 'company_id_eq')));
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [alertMessage, setAlertMessage] = useState<any>(null);

    function hasFilters(ref: any, field = 'company_id') {
        const tempPayloadRef = { ...(payloadRef.current || {}) };
        const _filters = (ref ? ref.current : tempPayloadRef.filters) || [];
        const company_id = _filters[field];
        return company_id || "";
    }

    function onLocationChange(event: any) {
        const { company, associate_company, location } = event;
        setLocationFilter({
            company_id_eq: company,
            associate_company_id_eq: associate_company,
            location_id_eq: location
        });
    }

    function search(event: any) {
        const _filters: any = {};
        const keys = Object.keys(event || {});
        if (keys.length > 0) {
            keys.forEach(key => {
                _filters[key] = event[key]
            });
        }
        setAdvanceSearchFilters(_filters);
    }

    function dismissAction() {
        setAction(ACTIONS.NONE);
        setActivity(null);
    }

    function MonthTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <>{row.month} ({row.year})</>
        )
    }

    function DueDateTmpl({ cell }: any) {
        const value = cell.getValue();
        return (
            <span className="text-warn" >{dayjs(value).format('DD-MM-YYYY')}</span>
        )
    }

    function FormStatusTmpl({ cell }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center position-relative">
                <span className={`status-${status} ellipse`}>{STATUS_MAPPING[status] || status}</span>
            </div>
        )
    }

    function ActionColumnElements({ cell }: any) {
        const row = cell.getData();

        return (
            <div className="d-flex flex-row align-items-center position-relative">
                {
                    hasUserAccess(USER_PRIVILEGES.AUDIT_SCHEDULE_UNBLOCK_BLOCKED) &&
                    <Icon className="mx-1" type="button" name="lock-open" text="Un-Block" data={row} action={handleUnblock} />
                }
            </div>
        )
    }

    function ActivityTypeTmpl({ cell }: any) {
        const value = cell.getValue() || ACTIVITY_TYPE.AUDIT;
        return (
            <div className="d-flex flex-row align-items-center justify-content-center position-relative">
                <Icon name={ACTIVITY_TYPE_ICONS[value]} text={value} />
            </div>
        )
    }

    const columns = [
        {
            title: "", field: "auditted", width: 40,
            formatter: reactFormatter(<ActivityTypeTmpl />)
        },
        {
            title: "Month & Year", field: "month", width: 140,
            formatter: reactFormatter(<MonthTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Act", field: "act.name", widthGrow: 2,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },

        {
            title: "Rule", field: "rule.name", widthGrow: 2,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Activity", field: "activity.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 2
        },
        {
            title: "Activity Type", field: "activity.activity_type",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Associate Company", field: "associate_company.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Location Name", field: "location.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Audit Due Date", field: "due_date", width: 120,
            formatter: reactFormatter(<DueDateTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Forms Status", field: "status", width: 160,
            formatter: reactFormatter(<FormStatusTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Actions", hozAlign: "center", width: 120,
            headerSort: false,
            formatter: reactFormatter(<ActionColumnElements />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            visible: hasUserAccess(USER_PRIVILEGES.AUDIT_SCHEDULE_UNBLOCK_BLOCKED)
        }
    ]

    const [tableConfig] = useState({
        paginationMode: 'remote',
        ajaxRequestFunc,
        columns,
        rowHeight: 'auto',
        selectable: true,
        paginate: true,
        initialSort: ['month desc']
    });

    function formatApiResponse(params: any, list: any[], totalRecords: number) {
        const { page, per } = params || {};
        const tdata = {
            data: list,
            total: totalRecords,
            last_page: Math.ceil(totalRecords / (per || 1)) || 1,
            page: page || 1
        };
        setData(tdata);
        return tdata;
    }

    function ajaxRequestFunc(url: any, config: any, params: any) {
        const { field, dir } = (params.sort || [])[0] || {};
        const _params = {
            page: params.page,
            per: params.size,
            sorts: [`${SortFields[field] || field || 'month'} ${dir || 'desc'}`],
            includes: INCLUDES_ASSOCIATIONS,
            include: INCLUDE_ASSOCIATION_COLUMNS
        };
        setParams(_params);
        setPayload({
            ...DEFAULT_PAYLOAD,
            ..._params,
            filters: {
                ...(lfRef.current || {}),
                ...(afRef.current || {}),
                ...(sfRef.current || {}),
                published_eq: false

            }
        });
        return Promise.resolve(formatApiResponse(params, activities, total));
    }

    function handlePageNav(_pagination: any) {
        const _params = { ..._pagination };
        setParams({ ..._params });
        setPayload({ ...payload, ..._params, filters: {...payload.filters, published_eq: false} })
    }

    function getAdvanceSearchPayload() {
        const _filters = {...(afRef.current || {})};
        return _filters;
    }

    function handleUnblock(_activity: any) {
        setActivity(_activity)
        setAction(ACTIONS.EDIT);
    }

    function handleBulkUpdate(e: any) {
        preventDefault(e);
        setAction(ACTIONS.BULK_EDIT);
    }

    function onSelectionChange(_selectedRows: any) {
        setSelectedRows(_selectedRows);
    }

    useEffect(() => {
        if (locationFilters) {
            setPayload({
                ...DEFAULT_PAYLOAD,
                sorts: ['month desc'],
                ...params,
                filters: {
                    ...locationFilters,
                    ...(afRef.current || {}),
                    ...(sfRef.current || {}),
                    published_eq: false
                }
            });
        }
    }, [locationFilters]);

    useEffect(() => {
        if (advaceSearchFilters) {
            setPayload({
                ...DEFAULT_PAYLOAD,
                sorts: ['month desc'],
                ...params,
                filters: {
                    ...(lfRef.current || {}),
                    ...advaceSearchFilters,
                    ...(sfRef.current || {}),
                    published_eq: false
                }
            });
        }
    }, [advaceSearchFilters]);

    useEffect(() => {
        if (!isFetching && payload) {
            setSelectedRows([]);
            setData(formatApiResponse(params, activities, total));
        }
    }, [isFetching])

    return (
        <>
            <MastersLayout title="Location Audit Un-Block Activities" breadcrumbs={breadcrumb}>
                <form className="p-0 mx-3 my-2">
                    <div className="card shadow border-0 p-2 mt-2 mb-3 filters">
                        <div className="d-flex flex-row m-0 align-items-end">
                            <AdminLocations onChange={onLocationChange} />
                            <div >
                                <AdvanceSearch fields={[FILTERS.MONTH]} payload={getAdvanceSearchPayload()} onSubmit={search} />
                            </div>
                            {
                                hasUserAccess(USER_PRIVILEGES.AUDIT_SCHEDULE_UNBLOCK_BLOCKED) &&
                                <div className="ms-auto">
                                    <button className="btn btn-success" onClick={handleBulkUpdate} disabled={(selectedRows || []).length === 0}>
                                        <div className="d-flex align-items-center">
                                            <Icon name={'lock-open'} />
                                            <span className="ms-2 text-nowrap">Un-Block</span>
                                        </div>
                                    </button>
                                </div>
                            }
                        </div>
                    </div>
                </form>
                <Table data={data} options={tableConfig} isLoading={isFetching} onSelectionChange={onSelectionChange} onPageNav={handlePageNav} />
            </MastersLayout>
            {
                !!alertMessage &&
                <AlertModal message={alertMessage} onClose={(e: any) => {
                    preventDefault(e);
                    setAlertMessage(null);
                }} />
            }
            {
                action === ACTIONS.EDIT && activity &&
                <UnBlockModal activity={activity} onSubmit={refetch} onClose={dismissAction} />
            }
            {
                action === ACTIONS.BULK_EDIT && (selectedRows || []).length > 0 &&
                <BulkUnBlockModal selected={selectedRows} onSubmit={refetch} onClose={dismissAction} />
            }
        </>
    );

}

export default LockUnLock;